import { client } from '../../../services/apiClient';
import { format } from 'date-fns';
import type {
  InsightsBrandFunnelTimeSeriesBucket,
  InsightsBrandFunnelsResponse,
  InsightsOverviewResponse,
  InsightsPlatformRow,
  InsightsPromptFunnelsResponse,
  InsightsTimeSeriesBucket,
} from '@brand24-original/llm-types';
import { ChatType } from '../../../types/monitor/ChatType';
import { PeriodType } from '../../../types/prompt/PeriodType';

const INSIGHTS_STATS_ENDPOINT = '/insights-stats';

export const getInsightsOverview = async (
  projectId: number,
  type: ChatType,
  from: Date,
  to: Date,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsOverviewResponse> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/overview/${projectId}/${type}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<InsightsOverviewResponse>(url);
  return data;
};

export const getInsightsTimeSeries = async (
  projectId: number,
  type: ChatType,
  period: PeriodType,
  from: Date,
  to: Date,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsTimeSeriesBucket[]> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/timeseries/${projectId}/${type}/${period}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<InsightsTimeSeriesBucket[]>(url);
  return data;
};

export const getInsightsPlatforms = async (
  projectId: number,
  type: ChatType,
  from: Date,
  to: Date,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsPlatformRow[]> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/platforms/${projectId}/${type}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<InsightsPlatformRow[]>(url);
  return data;
};

export const getInsightsBrandFunnels = async (
  projectId: number,
  type: ChatType,
  from: Date,
  to: Date,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsBrandFunnelsResponse> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/brand-funnels/${projectId}/${type}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<InsightsBrandFunnelsResponse>(url);
  return data;
};

export const getInsightsBrandFunnelsTimeSeries = async (
  projectId: number,
  type: ChatType,
  period: PeriodType,
  from: Date,
  to: Date,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsBrandFunnelTimeSeriesBucket[]> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/brand-funnels-timeseries/${projectId}/${type}/${period}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const { data } = await client(true, true).get<InsightsBrandFunnelTimeSeriesBucket[]>(url);
  return data;
};

export const getInsightsPromptFunnels = async (
  projectId: number,
  type: ChatType,
  from: Date,
  to: Date,
  page: number,
  rows: number,
  monitorPromptIds?: number[],
  filterChatTypes?: string[]
): Promise<InsightsPromptFunnelsResponse> => {
  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  let url = `${INSIGHTS_STATS_ENDPOINT}/prompt-funnels/${projectId}/${type}/${formattedFrom}/${formattedTo}`;
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('rows', String(rows));
  if (monitorPromptIds && monitorPromptIds.length > 0) {
    params.set('promptIds', monitorPromptIds.join(','));
  }
  if (filterChatTypes && filterChatTypes.length > 0) {
    params.set('models', filterChatTypes.join(','));
  }
  url += `?${params.toString()}`;

  const { data } = await client(true, true).get<InsightsPromptFunnelsResponse>(url);
  return data;
};
